import { useState, useEffect } from "react";

/**
 * Result of {@link useMediaQueryWithMount}.
 *
 * `mounted` is `false` during SSR and the first client paint, flipping to
 * `true` after the first effect runs. Callers that must avoid a layout flash
 * (e.g. desktop-table-then-mobile-cards swap) should hold a neutral state
 * until `mounted` is `true`, then trust `matches`.
 */
export interface MediaQueryWithMount {
  matches: boolean;
  mounted: boolean;
}

/**
 * SSR-safe media query hook that also reports whether the component has
 * mounted on the client.
 *
 * During SSR and the first paint `matches` is the result of
 * `window.matchMedia` when available, otherwise `false`, and `mounted` is
 * `false`. Because the server cannot know the real viewport, consumers that
 * branch layout on the match (mobile vs desktop) should render a neutral
 * placeholder while `mounted === false` to prevent a hydration flash, rather
 * than committing to the SSR default.
 */
export function useMediaQueryWithMount(query: string): MediaQueryWithMount {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);

    // Sync to the real value on mount, then mark mounted so consumers can
    // safely branch on `matches` without an SSR/first-paint flash.
    setMatches(media.matches);
    setMounted(true);

    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return { matches, mounted };
}

/**
 * Boolean media query hook.
 *
 * Returns `false` during SSR/first paint (the viewport is unknown on the
 * server), then the real match after mount. When the first-paint value would
 * cause a visible layout swap, prefer {@link useMediaQueryWithMount} and gate
 * on its `mounted` flag.
 */
export function useMediaQuery(query: string): boolean {
  return useMediaQueryWithMount(query).matches;
}
